import { FC, useCallback, useEffect, useRef, useState } from "react";
import {
  Layout,
  Row,
  Col,
  Card,
  Typography,
  DatePicker,
  Select,
  Button,
  Space,
} from "antd";
import { useLocation } from "react-router-dom";
import {
  SunOutlined,
  MoonOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store/store";
import { updateSelectedHospital } from "../../../models/hospitalSlice";
import SummaryModal from "./SummaryModal";
import ViewMoreModal from "./ViewMoreModal";
import FetchSchemeActions from "./FetchSchemeActions";
import ReconFileUpload from "./ReconFileUpload";
import ReportStatus from "./ReportStatus";
import { IHospital } from "../../../interfaces/IHospital";
import { errorMessage, successMessage } from "../../../utils/notificationUtils";
import { message } from "antd";
import { UploadDocument } from "../../../models/EnhancementSlice";
import {
  fetchInitialEODBODConfig,
  fetchProcessedFiles,
  fetchProcessHistory,
  getFileMapperByHospital,
  getPortalSummaryByActivityId,
  saveFileMapper,
  saveMapping,
  updateFileMapperByHospital,
} from "../../../apis/reconciliationService";
import ConfirmSubmissionModalStage from "../../modals/ConfirmSubmissionModalStage";
import dayjs, { Dayjs } from "dayjs";

const { Content } = Layout;
const { Text } = Typography;

type EODBODScreenProps = {
  availableHospitals: Array<IHospital> | undefined;
  selectedHospital: number | null;
  setSelectedHospital: any;
  availableSchemes: any;
  selectedScheme: any;
  setSelectedScheme: any;
};

const EODBODScreen: FC<EODBODScreenProps> = (props) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const [bodEod, setBodEod] = useState("");
  const [selectedPortal, setSelectedPortal] = useState<number | null>(null);
  const [selectedSchemeName, setSelectedSchemeName] = useState<any>(null);
  const [activeTab, setActiveTab] = useState("1");
  const [openSummaryModal, setOpenSummaryModal] = useState(false);
  const [openViewMoreModal, setOpenViewMoreModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [reconciliationFile, setReconciliationFile] = useState<any>(null);
  const [documentUrl, setDocumentUrl] = useState<any>(null);
  const [selectedFile, setSelectedFile] = useState<{
    file: File;
  } | null>(null);
  const [isFileMappingLoading, setIsFileMappingLoading] =
    useState<boolean>(false);
  const [reconData, setReconData] = useState<any>([]);
  const [loadingProcessedFileList, setLoadingProcessedFileList] =
    useState(false);
  const [totalListCount, setTotalListCount] = useState<any>(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
  });
  const [viewMappingModalLoader, setViewMappingModalLoader] =
    useState<boolean>(false);
  const [hospitalColumns, setHospitalColumns] = useState<Array<string> | null>(
    null,
  );
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [confirmationMappings, setConfirmationMappings] = useState<any[]>([]);
  const reconciliationRef = useRef<any>(null);
  const [dbColumns, setDBColumns] = useState<any>(null);
  const [showMapping, setShowMapping] = useState<boolean>(false);
  const [showMappingModal, setShowMappingModal] = useState<boolean>(false);
  const [viewMappingModal, setViewMappingModal] = useState<boolean>(false);
  const [existingMappings, setExistingMappings] = useState<any[]>([]);
  const [isMappingLoading, setIsMappingLoading] = useState<boolean>(false);
  const [response, setResponse] = useState<any>(false);
  const [isFileReading, setIsFileReading] = useState<boolean>(false);
  const [isViewMoreModalDataLoading, setIsViewMoreModalDataLoading] =
    useState<boolean>(false);
  const [viewMoreModalData, setViewMoreModalData] = useState<any[]>([]);
  const [initialEODBODConfig, setInitialEODBODConfig] = useState<any>({});
  const [viewMoreDataTotal, setViewMoreDataTotal] = useState<number>(0);
  const [summaryData, setSummaryData] = useState<any>({});
  const [tilesData, setTilesData] = useState<any[]>([]);
  const [isSummaryModalDataLoading, setIsSummaryModalDataLoading] =
    useState<boolean>(false);

  useEffect(() => {
    const sessionTab = sessionStorage.getItem("eod-active-tab");
    const sessionSelectedBodEodValue = sessionStorage.getItem(
      "selected-bod-eod-value",
    );
    const locationTab = (location.state as any)?.returnToTab;

    if (locationTab === "2" || sessionTab === "2") {
      setActiveTab("2");

      if (sessionSelectedBodEodValue) {
        setBodEod(sessionSelectedBodEodValue);
        getInitialEODBODConfig(sessionSelectedBodEodValue);
      }

      sessionStorage.removeItem("eod-active-tab");
      sessionStorage.removeItem("selected-bod-eod-value");
    }
  }, [location.state]);

  const isBodEodEnabled =
    selectedDate && props?.selectedHospital && props?.selectedHospital !== -1;

  const [pendingMappingParams, setPendingMappingParams] = useState<{
    mappings: any;
    sequence: any;
  } | null>(null);

  const getInitialEODBODConfig = async (eodBodProcessType?: string) => {
    try {
      const res = await fetchInitialEODBODConfig(
        props?.selectedHospital,
        eodBodProcessType ?? bodEod,
        selectedDate?.format("YYYY-MM-DD") || null,
      );
      setInitialEODBODConfig(res?.data || {});
    } catch (err) {
      setInitialEODBODConfig({});
    }
  };

  const handleFileChange = (e: any) => {
    const allowedTypes = [
      "application/vnd.ms-excel", // .xls
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "text/csv", // .csv
    ];

    let file: File | undefined;

    if (Array.isArray(e)) {
      return e;
    }

    if (e?.file) {
      file = e.file;
    } else if (e?.target?.files?.[0]) {
      file = e.target.files[0];
    }

    if (file) {
      if (allowedTypes.includes(file.type)) {
        setSelectedFile({ file });
      } else {
        errorMessage("Only Excel files (.xlsx) are allowed.");
        return null;
      }
      return file;
    }

    return null;
  };

  useEffect(() => {
    if (isFileReading) {
      message.warning("File reading is in progress...");
    }
  }, [isFileReading]);

  const handlePaginationChangeListModal = async (
    pageNumber: number,
    pageSize: number,
  ) => {
    getProcessedFileListContent(pageNumber, pageSize);
  };

  const handleHospitalChange = (hospitalId: number) => {
    if (props?.selectedHospital !== hospitalId) {
      dispatch(updateSelectedHospital(hospitalId));
      setBodEod("");
    }
    props?.setSelectedHospital(hospitalId);
  };

  const handleSchemeChange = useCallback(
    (schemeId: number, schemeName: string, portalId: any) => {
      props?.setSelectedScheme(schemeId);
      const name = (schemeName || "").toUpperCase();
      setSelectedSchemeName(name);
      setSelectedPortal(portalId);
    },
    [],
  );

  const getProcessedFileListContent = async (
    pageNumber: number = 1,
    pageSize: number = 20,
  ) => {
    try {
      if (props?.selectedHospital) {
        const hospitalId = props?.selectedHospital;
        setLoadingProcessedFileList(true);
        const response = await fetchProcessedFiles({
          hospitalId,
          page: pageNumber - 1,
          size: pageSize,
        });
        setReconData(response?.data);
        setTotalListCount(response?.totalElements);
        setLoadingProcessedFileList(false);
      }
    } catch (err) {
      setReconData([]);
      setTotalListCount(0);
      setLoadingProcessedFileList(false);
    }
  };

  useEffect(() => {
    const handleUpload = async () => {
      const CollectPostData = new FormData();
      if (props?.selectedHospital) {
        CollectPostData.append(
          "hospitalId",
          props?.selectedHospital.toString(),
        );
      }
      CollectPostData.append("patientId", "");
      CollectPostData.append("caseId", "");
      CollectPostData.append("visitId", "");

      if (selectedFile?.file) {
        CollectPostData.append("file", selectedFile?.file);
      }

      try {
        const documentRes = await dispatch(
          UploadDocument({
            formData: CollectPostData,
            accessToken: "",
          }),
        ).unwrap();

        setDocumentUrl(documentRes?.[0]?.documentUrl);
        message.success(`Excel File uploaded successfully`);
      } catch (error) {
        console.error("Failed to upload document:", error);
        setDocumentUrl(null);
        setSelectedFile(null);
        errorMessage("Please, Upload File Again");
      }
    };

    if (selectedFile?.file) {
      handleUpload();
    }
  }, [selectedFile]);

  useEffect(() => {
    if (activeTab === "2") getProcessedFileListContent();
  }, [props?.selectedHospital, activeTab]);

  const saveFileMapperData = async () => {
    if (!reconciliationFile?.sourceFileType) {
      errorMessage("Please, Select Reconciliation File Option");
      return;
    }

    if (!selectedFile?.file) {
      errorMessage("Please, Upload File");
      return;
    }

    const payload = {
      s3Url: documentUrl,
      hospitalId: props?.selectedHospital,
      fileType: reconciliationFile?.sourceFileType,
      fileName: selectedFile?.file.name,
      startDate: selectedDate?.format("DD-MM-YYYY") || null,
      endDate: selectedDate?.format("DD-MM-YYYY") || null,
      orchestratorId: initialEODBODConfig?.orchestratorId || -1,
      eodBodProcessType: bodEod,
      sourceRegisterType: reconciliationFile?.sourceFileType
    };

    try {
      setIsFileMappingLoading(true);
      const res = await saveFileMapper(payload);
      setIsFileMappingLoading(false);

      if (res?.status) {
        setHospitalColumns(null);
        setDBColumns(null);
        message.success("File processed.");
      } else {
        setHospitalColumns(res?.excelColumnsData);
        setDBColumns(res?.dbColumnsData);
        setShowMapping(true);
        message.error(res?.message);
      }
      setSelectedFile(null);
      setDocumentUrl(null);
      if (props?.selectedHospital) {
        getProcessedFileListContent();
      }
    } catch (error) {
      setIsFileMappingLoading(false);
      setHospitalColumns(null);
      setDBColumns(null);
      setSelectedFile(null);
      setDocumentUrl(null);
      errorMessage("Please, Try again later");
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setDocumentUrl(null);
  };

  // const handleGetFileMapperByHospital = async () => {
  //   if (!reconciliationFile) {
  //     errorMessage("Please, Select Reconciliation File Option");
  //     return;
  //   }

  //   if (!selectedFile?.file) {
  //     errorMessage("Please, Upload File");
  //     return;
  //   }

  //   try {
  //     const payload = {
  //       s3Url: documentUrl,
  //       hospitalId: props?.selectedHospital,
  //       fileType: reconciliationFile,
  //       fileName: selectedFile?.file.name,
  //     };

  //     setViewMappingModalLoader(true);
  //     const res = await getFileMapperByHospital(payload);
  //     setViewMappingModalLoader(false);
  //     if (
  //       !res?.FileMapperResponseDto?.status &&
  //       res?.FileMapperResponseDto?.excelColumnsData &&
  //       res?.FileMapperResponseDto?.dbColumnsData
  //     ) {
  //       setHospitalColumns(res?.FileMapperResponseDto?.excelColumnsData);
  //       setDBColumns(res?.FileMapperResponseDto?.dbColumnsData);
  //       setShowMappingModal(true);
  //       errorMessage(
  //         "Mapping doesn't exist for the selected hospital file. Please create a new mapping.",
  //       );
  //       setViewMappingModal(false);
  //       setExistingMappings([]);
  //     } else {
  //       setHospitalColumns(null);
  //       setDBColumns(res?.["DB columns"]);
  //       setShowMappingModal(false);
  //       setConfirmationMappings([]);
  //       setViewMappingModal(true);
  //       setExistingMappings(res?.FileMapper);
  //     }
  //   } catch (err) {
  //     errorMessage("Please, Try again later");
  //     setHospitalColumns(null);
  //     setDBColumns(null);
  //     setShowMappingModal(false);
  //     setConfirmationMappings([]);
  //     setViewMappingModal(false);
  //     setExistingMappings([]);
  //     setViewMappingModalLoader(false);
  //   }
  // };

  // const handleNewGetFileMapperByHospital = async () => {
  //   if (!reconciliationFile) {
  //     errorMessage("Please, Select Reconciliation File Option");
  //     return;
  //   }

  //   if (!selectedFile?.file) {
  //     errorMessage("Please, Upload File");
  //     return;
  //   }

  //   try {
  //     const payload = {
  //       s3Url: documentUrl,
  //       hospitalId: props?.selectedHospital,
  //       fileType: reconciliationFile,
  //       fileName: selectedFile?.file.name,
  //     };

  //     setIsFileReading(true);
  //     const res = await getFileMapperByHospital(payload);
  //     if (
  //       !res?.FileMapperResponseDto?.status &&
  //       res?.FileMapperResponseDto?.excelColumnsData &&
  //       res?.FileMapperResponseDto?.dbColumnsData
  //     ) {
  //       setHospitalColumns(res?.FileMapperResponseDto?.excelColumnsData);
  //       setDBColumns(res?.FileMapperResponseDto?.dbColumnsData);
  //       setShowMappingModal(true);
  //       errorMessage(
  //         "Mapping doesn't exist for the selected hospital file. Please create a new mapping.",
  //       );
  //       setViewMappingModal(false);
  //       setExistingMappings([]);
  //     } else {
  //       setHospitalColumns(null);
  //       setDBColumns(res?.["DB columns"]);
  //       setShowMappingModal(false);
  //       setConfirmationMappings([]);
  //       setExistingMappings(res?.FileMapper);
  //       successMessage("Mapping already exists for this register");
  //     }
  //   } catch (err) {
  //     errorMessage("Please, Try again later");
  //     setHospitalColumns(null);
  //     setDBColumns(null);
  //     setShowMappingModal(false);
  //     setConfirmationMappings([]);
  //     setViewMappingModal(false);
  //     setExistingMappings([]);
  //   } finally {
  //     setIsFileReading(false);
  //   }
  // };

  // useEffect(() => {
  //   if (documentUrl && reconciliationFile && activeTab === "2") {
  //     handleNewGetFileMapperByHospital();
  //   }
  // }, [documentUrl, reconciliationFile, props?.selectedHospital, activeTab]);

  // const handleUpdateMappings = async (updatedMappings: any[]) => {
  //   try {
  //     const res = await updateFileMapperByHospital(updatedMappings);
  //     successMessage("Mappings updated successfully.");
  //     setViewMappingModal(false);
  //   } catch (err) {
  //     errorMessage("Error updating mappings. Please try again later.");
  //   }
  // };

  // const handleNewSaveMapping = async ({
  //   mappings,
  //   sequence,
  // }: {
  //   mappings: any;
  //   sequence: any;
  // }) => {
  //   // Validation: At least one mapping, one sequence, and one seqNo
  //   if (!mappings || mappings?.length === 0) {
  //     errorMessage("Please select at least one mapping.");
  //     return;
  //   }
  //   try {
  //     const data = {
  //       hospitalId: props?.selectedHospital,
  //       fileType: reconciliationFile,
  //       mappingColumnRequests: mappings,
  //       sequence: sequence,
  //       startDate: selectedDate?.format("DD-MM-YYYY") || null,
  //       endDate: selectedDate?.format("DD-MM-YYYY") || null,
  //     };

  //     setIsMappingLoading(true);
  //     const res = await saveMapping(data);
  //     setIsMappingLoading(false);
  //     setResponse(true);

  //     successMessage("Mapping Saved Successfully");
  //     setShowMapping(false);
  //     setHospitalColumns(null);
  //     setShowMappingModal(false);
  //     setConfirmationMappings([]);
  //     setDBColumns(null);
  //   } catch (err) {
  //     errorMessage("Please, Try again later");
  //     setIsMappingLoading(false);
  //     setResponse(false);
  //   }
  // };

  // const handleSave = async () => {
  //   if (pendingMappingParams) {
  //     if (showMappingModal) {
  //       await handleNewSaveMapping(pendingMappingParams);
  //     } else {
  //       await handleSaveMapping(pendingMappingParams);
  //     }
  //     setConfirmationModal(false);
  //     setPendingMappingParams(null);
  //   }
  // };

  // const handleSaveMapping = async ({
  //   mappings,
  //   sequence,
  // }: {
  //   mappings: any;
  //   sequence: any;
  // }) => {
  //   // Validation: At least one mapping, one sequence, and one seqNo
  //   if (!mappings || mappings?.length === 0) {
  //     errorMessage("Please select at least one mapping.");
  //     return;
  //   }
  //   try {
  //     const data = {
  //       hospitalId: props?.selectedHospital,
  //       fileType: reconciliationFile,
  //       mappingColumnRequests: mappings,
  //       sequence: sequence,
  //       startDate: selectedDate?.format("DD-MM-YYYY") || null,
  //       endDate: selectedDate?.format("DD-MM-YYYY") || null,
  //     };

  //     setIsMappingLoading(true);
  //     const res = await saveMapping(data);
  //     setIsMappingLoading(false);
  //     setResponse(true);

  //     successMessage("Mapping Saved Successfully");
  //     successMessage("Please, Re-upload the file");
  //     setShowMapping(false);
  //     setHospitalColumns(null);
  //     setDBColumns(null);
  //     setSelectedFile(null);
  //     setDocumentUrl(null);
  //     // Call handleResetData in child after successful mapping save
  //     if (
  //       reconciliationRef.current &&
  //       reconciliationRef.current.handleResetData
  //     ) {
  //       reconciliationRef.current.handleResetData();
  //     }
  //   } catch (err) {
  //     errorMessage("Please, Try again later");
  //     setIsMappingLoading(false);
  //     setResponse(false);
  //   }
  // };

  // const handleMapping = ({
  //   mappings,
  //   sequence,
  // }: {
  //   mappings: any;
  //   sequence: any;
  // }) => {
  //   if (!mappings || mappings?.length === 0) {
  //     errorMessage("Please select at least one mapping.");
  //     setResponse(false);
  //     return false;
  //   }

  //   setPendingMappingParams({ mappings, sequence });
  //   setConfirmationMappings(mappings);
  //   setConfirmationModal(true);
  // };

  const fetchViewMoreModalData = async (payload: any) => {
    try {
      setIsViewMoreModalDataLoading(true);
      const response = await fetchProcessHistory(payload);
      setViewMoreModalData(response?.data?.data || []);
      setViewMoreDataTotal(response?.data?.totalElements || 0);
    } catch (error: any) {
      const messageText =
        error?.message || "Failed to load process history for View More";
      errorMessage(messageText);
      setViewMoreModalData([]);
      setViewMoreDataTotal(0);
    } finally {
      setIsViewMoreModalDataLoading(false);
    }
  };

  const handleViewMoreClick = () => {
    setOpenViewMoreModal(true);
  };

  const typeToNameMapCghsNew: Record<string, string> = {
    PREAUTH_TO_BE_SUBMITTED: "Preauth to be Submitted",
    PREAUTH_PENDING: "Preauth Pending",
    UNDER_TREATMENT: "Under Treatment",
    CLAIMS_TO_BE_SUBMITTED: "Claims to Be Submitted",
    PREAUTH_QUERY: "Preauth Query",
    CLAIM_QUERY: "Claim Query",
    CLAIM_REJECTED: "Claim Rejected",
    PENDING_WITH_PAYER: "Pending with Payer",
    CLAIM_PAID: "Claim Paid",
    CLAIMS_SENT_TO_BANK: "Claims sent to Bank",
    CLAIM_DATA_EXPORT: "Claim Data",
  };

  const typeToNameMapEchs: Record<string, string> = {
    ECHS_SETTLEMENT: "Portal Settlement",
    ECHS_QUERY: "Portal Query",
    ECHS_CLAIM_PENDING: "Claim Pending",
    ECHS_PENDING_INTIMATION: "Pending Intimation",
    ECHS_CLAIM_DATA_EXPORT: "Claim Data",
  };

  const handleSummaryClicked = async (record: any) => {
    try {
      setOpenSummaryModal(true);
      setIsSummaryModalDataLoading(true);
      const activityId = record?.id;

      const res = await getPortalSummaryByActivityId(
        activityId,
        record?.hospitalId,
        record?.schemeId,
      );

      setSummaryData({
        processedRecords: record.processedRecords,
        scheme: record.schemeName,
        hospitalId: record.hospitalId,
        schemeId: record.schemeId,
      });

      if (record.schemeName === "CGHS NEW") {
        const formattedData = Object.keys(typeToNameMapCghsNew).map((key) => {
          const matchedItem = res?.find(
            (item: any) => item.type?.toUpperCase() === key,
          );

          return {
            type: key,
            name: typeToNameMapCghsNew[key],
            recordsFetched: matchedItem?.recordsFetched ?? 0,
            ...(matchedItem || {}),
          };
        });

        setTilesData(formattedData);
      } else if (record.schemeName === "ECHS") {
        const formattedData = Object.keys(typeToNameMapEchs).map((key) => {
          const matchedItem = res?.find(
            (item: any) => item.type?.toUpperCase() === key,
          );

          return {
            type: key,
            name: typeToNameMapEchs[key],
            recordsFetched: matchedItem?.recordsFetched ?? 0, // 👈 default 0 if not found
            ...matchedItem, // optional: include other fields if exist
          };
        });

        setTilesData(formattedData);
      }

      setIsSummaryModalDataLoading(false);
    } catch (err) {
      errorMessage("Failed to fetch summary details. Please try again later.");
      setIsSummaryModalDataLoading(false);
    }
  };

  return (
    <Layout style={{ minHeight: "100vh", background: "#f6f8fb" }}>
      <Content style={{ padding: "24px 40px" }}>
        <Card style={{ marginBottom: 24 }}>
          <Row gutter={16} align="middle">
            <Col span={5}>
              <Text strong>Date</Text>
              <DatePicker
                value={selectedDate}
                format="DD-MM-YYYY"
                allowClear
                style={{ width: "100%", marginTop: 5 }}
                onChange={(date) => {
                  setSelectedDate(date);
                  setBodEod("");
                }}
              />
            </Col>

            <Col span={6}>
              <Text strong>Hospital</Text>
              <Select
                showSearch
                placeholder="Select a Hospital"
                optionFilterProp="label"
                style={{ width: "100%", marginTop: 5 }}
                value={props?.selectedHospital}
                options={
                  props?.availableHospitals &&
                  props?.availableHospitals?.map((hospital) => ({
                    label: hospital.label,
                    value: hospital.value,
                  }))
                }
                onChange={handleHospitalChange}
              />
            </Col>

            <Col span={13}>
              <Row
                gutter={12}
                justify="end"
                style={{ marginTop: 30, width: "100%" }}
              >
                {[
                  {
                    label: "BOD (Beginning of Day)",
                    value: "BOD",
                    icon: <SunOutlined />,
                  },
                  {
                    label: "EOD (End of Day)",
                    value: "EOD",
                    icon: <MoonOutlined />,
                  },
                ].map((item) => {
                  // const isDisabled =
                  //   !isBodEodEnabled || (bodEod && bodEod !== item.value);

                  // const isDisabled = !initialEODBODConfig?.showFetchActions;
                  const isDisabled = false;

                  return (
                    <Col key={item.value}>
                      <Card
                        hoverable={!isDisabled}
                        onClick={() => {
                          !isDisabled && setBodEod(item.value);
                          getInitialEODBODConfig(item.value);
                        }}
                        // onClick={() => setBodEod(item.value)}
                        style={{
                          textAlign: "center",
                          padding: "6px 12px",
                          height: "45px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border:
                            bodEod === item.value
                              ? "2px solid rgb(0, 76, 143)"
                              : "1px solid #e5e5e5",
                          background:
                            bodEod === item.value ? "#f0f6ff" : "#fff",
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          opacity: isDisabled ? 0.5 : 1,
                          // cursor: "pointer",
                          // opacity: 1,
                          fontSize: "13px",
                          minWidth: "150px",
                        }}
                      >
                        <Space>
                          {item.icon}
                          <Text strong>{item.label}</Text>
                          {bodEod === item.value && (
                            <CheckCircleFilled style={{ color: "#00549d" }} />
                          )}
                        </Space>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Col>
          </Row>
        </Card>

        {props?.selectedHospital &&
          props?.selectedHospital !== -1 &&
          selectedDate &&
          bodEod && (
            <>
              <Row
                gutter={20}
                style={{
                  borderBottom: "0.4px solid #D1D1D1",
                  marginBottom: 20,
                }}
              >
                {[
                  { key: "1", label: "Fetch & Scheme Actions" },
                  { key: "2", label: "Recon File Upload" },
                  { key: "3", label: "Report & Status" },
                ].map((tab) => (
                  <Col key={tab.key}>
                    <Button
                      disabled={
                        tab.key === "2" && !initialEODBODConfig?.showReconUpload
                      }
                      type={activeTab === tab.key ? "primary" : "default"}
                      onClick={() => setActiveTab(tab.key)}
                      style={
                        activeTab === tab.key
                          ? {
                              background: "#00549d",
                              borderTopLeftRadius: "15px",
                              borderTopRightRadius: "15px",
                              borderBottomLeftRadius: "0px",
                              borderBottomRightRadius: "0px",
                              fontWeight: "bold",
                              height: "36px",
                              boxShadow: "0px -1px 4px 0px #00000040 inset",
                            }
                          : {
                              border: "none",
                              boxShadow: "none",
                              height: "36px",
                              color: "#575757",
                            }
                      }
                    >
                      {tab.label}
                    </Button>
                  </Col>
                ))}
              </Row>

              {activeTab === "1" && (
                <FetchSchemeActions
                  availableSchemes={props?.availableSchemes}
                  selectedScheme={props?.selectedScheme}
                  selectedPortal={selectedPortal}
                  handleSchemeChange={handleSchemeChange}
                  selectedSchemeName={selectedSchemeName}
                  handleSummaryClicked={handleSummaryClicked}
                  handleViewMoreClick={handleViewMoreClick}
                  selectedHospital={props?.selectedHospital}
                  bodEod={bodEod}
                  selectedDate={selectedDate}
                  initialEODBODConfig={initialEODBODConfig}
                  getInitialEODBODConfig={getInitialEODBODConfig}
                  activeTab={activeTab}
                />
              )}

              {activeTab === "2" && (
                <ReconFileUpload
                  saveFileMapperData={saveFileMapperData}
                  setReconciliationFile={setReconciliationFile}
                  selectedFile={selectedFile}
                  handleFileChange={handleFileChange}
                  handleRemoveFile={handleRemoveFile}
                  reconciliationFile={reconciliationFile}
                  isFileMappingLoading={isFileMappingLoading}
                  reconData={reconData}
                  totalListCount={totalListCount}
                  loadingProcessedFileList={loadingProcessedFileList}
                  onPaginationChange={handlePaginationChangeListModal}
                  pagination={pagination}
                  setPagination={setPagination}
                  // viewMappingModalLoader={viewMappingModalLoader}
                  // handleGetFileMapperByHospital={handleGetFileMapperByHospital}
                  // showMapping={showMapping}
                  // hospitalColumns={hospitalColumns}
                  // dbColumns={dbColumns}
                  // showMappingModal={showMappingModal}
                  // setShowMappingModal={setShowMappingModal}
                  // viewMappingModal={viewMappingModal}
                  // setViewMappingModal={setViewMappingModal}
                  // existingMappings={existingMappings}
                  // handleUpdateMappings={handleUpdateMappings}
                  // handleSaveMapping={handleMapping}
                  ref={reconciliationRef}
                  setReconData={setReconData}
                  bodEod={bodEod}
                  selectedHospital={props?.selectedHospital}
                  availableHospitals={props?.availableHospitals || []}
                />
              )}

              {activeTab === "3" && (
                <ReportStatus
                  selectedHospital={props?.selectedHospital}
                  selectedDate={selectedDate}
                  onOpenViewMoreModal={() => setOpenViewMoreModal(true)}
                />
              )}
            </>
          )}

        <SummaryModal
          open={openSummaryModal}
          onClose={() => setOpenSummaryModal(false)}
          summaryData={summaryData}
          tilesData={tilesData}
          isSummaryModalDataLoading={isSummaryModalDataLoading}
        />
        <ViewMoreModal
          open={openViewMoreModal}
          onClose={() => setOpenViewMoreModal(false)}
          availableHospitals={props?.availableHospitals || []}
          selectedHospital={props?.selectedHospital}
          selectedScheme={props?.selectedScheme}
          isViewMoreModalDataLoading={isViewMoreModalDataLoading}
          viewMoreModalData={viewMoreModalData}
          fetchViewMoreModalData={fetchViewMoreModalData}
          viewMoreDataTotal={viewMoreDataTotal}
        />
      </Content>

      {/* <ConfirmSubmissionModalStage
        open={confirmationModal}
        title={"Are you sure you want to submit this mapping?"}
        handleCancel={() => setConfirmationModal(false)}
        handleSave={handleSave}
        isLoading={isMappingLoading}
        mappings={confirmationMappings}
        width={400}
      /> */}
    </Layout>
  );
};

export default EODBODScreen;
